/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   Shell.tsx
 *     Description     :   Responsive authenticated shell — full-width header (hamburger/collapse toggle,
 *                         logo + title, identity, sign-out), a NavSidebar + content row (mobile drawer
 *                         with backdrop; desktop collapsible rail), and a full-width footer. Adapts
 *                         across desktop / tablet / mobile. The header's second row is the switch
 *                         banner (spec 044), so every page shows the switches.
 *     Module          :   005-web-shell
 *     Component Type  :   Component
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 *     1.1         2026-06-26      Peter Dobler        Responsive: collapsible sidebar + mobile drawer; title "Migration Control Center 2.0"
 *     1.2         2026-10-25      Peter Dobler        Spec 044 T015: the switch banner as the header's second row, on every page
 *     1.3         2026-10-25      Peter Dobler        Spec 044: the switches collapsed into the title row (operator: "collaps it into the top header"); the arrow opens the full view as the second row; below sm the title text is hidden, like the logo, to make room
 *     1.4         2026-10-25      Peter Dobler        The shell takes the visible viewport's height (h-dvh), so a phone's address bar no longer pushes the footer out of view (operator: "there is a footer, but it's outside the viewing area")
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   react react-router-dom lucide-react
 *     Internal        :   useAuth NavSidebar SwitchBanner
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     Shell
 *
 * =========================================================================================
 */

import { useId, useState } from "react";
import { Outlet } from "react-router-dom";
import { Menu, PanelLeft } from "lucide-react";
import { useAuth } from "../auth/useAuth.js";
import { useCurrentUser } from "../auth/UserProvider.js";
import { isDevBypass } from "../auth/token.js";
import { useDevRole } from "../auth/devRole.js";
import { Badge } from "./ui/index.js";
import { APP_ROLES, type AppRole } from "@nextgenmigration/shared/user";
import { NavSidebar } from "./NavSidebar.js";
import { SwitchBanner, SwitchSummary } from "./SwitchBanner.js";

/** Build version, injected per container deploy (separate values for prod/dev); "dev" locally. */
const APP_VERSION = (import.meta.env["VITE_APP_VERSION"] as string | undefined) ?? "dev";

/** Authenticated, responsive page layout: full-width header, sidebar + content row, full-width footer. */
export function Shell(): React.JSX.Element {
  const { account, signOut } = useAuth();
  const { user } = useCurrentUser();
  const devRole = useDevRole((s) => s.role);
  const setDevRole = useDevRole((s) => s.setRole);
  const bypass = isDevBypass();
  const year = new Date().getFullYear();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Spec 044: the switches are a summary in the title row; its arrow opens the full view below it.
  const [switchesOpen, setSwitchesOpen] = useState(false);
  const switchPanelId = useId();

  return (
    // The visible viewport's height (100dvh), not 100vh: a phone's 100vh counts the area under its address bar, and
    // with the overflow clipped the footer sat below the screen (operator 2026-10-25).
    <div className="flex h-dvh flex-col overflow-hidden bg-[var(--color-bg-page)]">
      {/* Full-width header: the title row with the switch summary, and, while it is open, the full switch view
          (spec 044), on every page. */}
      <header className="relative z-10 border-b border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-sm">
        <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            {/* Mobile/tablet: open the nav drawer. */}
            <button
              type="button"
              onClick={() => setMobileNavOpen((o) => !o)}
              aria-label="Toggle navigation"
              data-testid="nav-toggle"
              className="rounded-md p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* Desktop: collapse the sidebar to icons. */}
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-label="Collapse sidebar"
              data-testid="nav-collapse"
              className="hidden rounded-md p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] lg:inline-flex"
            >
              <PanelLeft className="h-5 w-5" />
            </button>
            <img src="/logo.png" alt="Dobler Data Solutions" className="hidden h-8 w-auto sm:block" />
            {/* Below sm the switch summary takes the title's room (measured at 390 px: 9 px were left, a clipped "M"),
                so the title shows from sm up, like the logo. */}
            <span className="hidden truncate text-base font-semibold text-[var(--color-text-primary)] sm:inline sm:text-lg">
              Migration Control Center 2.0
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <SwitchSummary
              expanded={switchesOpen}
              onToggle={() => setSwitchesOpen((open) => !open)}
              panelId={switchPanelId}
            />
            {bypass && (
              <label
                className="hidden items-center gap-1.5 text-xs text-[var(--color-text-secondary)] sm:flex"
                title="Local-only: preview role-gated UI"
              >
                Role&nbsp;(dev)
                <select
                  value={devRole ?? "__default__"}
                  onChange={(e) => setDevRole(e.target.value === "__default__" ? null : (e.target.value as AppRole))}
                  className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-1.5 py-0.5 text-xs text-[var(--color-text-primary)]"
                  data-testid="dev-role-select"
                >
                  <option value="__default__">Default</option>
                  {APP_ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
            )}
            {user && (
              <Badge variant={user.role === "Administrator" ? "info" : "default"} data-testid="caller-role">
                {user.role}
              </Badge>
            )}
            {account && (
              <span className="hidden text-sm text-[var(--color-text-secondary)] md:inline" data-testid="caller-name">
                {account.name}
              </span>
            )}
            <button
              type="button"
              onClick={() => void signOut()}
              className="text-sm text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] focus:underline focus:outline-none"
              data-testid="sign-out-button"
            >
              Sign out
            </button>
          </div>
        </div>
        {switchesOpen && <SwitchBanner id={switchPanelId} />}
      </header>

      {/* Sidebar + content row (relative so the mobile drawer + backdrop scope to it, below the header) */}
      <div className="relative flex flex-1 overflow-hidden">
        {mobileNavOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileNavOpen(false)}
            className="absolute inset-0 z-30 bg-black/40 lg:hidden"
          />
        )}
        <NavSidebar collapsed={collapsed} mobileOpen={mobileNavOpen} onNavigate={() => setMobileNavOpen(false)} />
        <main className="flex-1 overflow-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>

      {/* Full-width footer */}
      <footer className="flex items-center justify-between gap-2 border-t border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-2 text-xs text-[var(--color-text-secondary)] sm:px-6">
        <span className="truncate" data-testid="footer-copyright">
          © {String(year)} Dobler Data Solutions, LLC. All rights reserved.
        </span>
        <span className="shrink-0" data-testid="footer-version">Version {APP_VERSION}</span>
      </footer>
    </div>
  );
}
